import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import Login from './pages/Login';
import MainLayout from './components/Layout/MainLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { RESOURCES, getFirstAccessibleRoute } from './utils/rolePermissions';
import './App.css';

// Lazy load all page components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Products = lazy(() => import('./pages/Products'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Sales = lazy(() => import('./pages/Sales'));
const Returns = lazy(() => import('./pages/Returns'));
const Purchases = lazy(() => import('./pages/Purchases'));
const Platforms = lazy(() => import('./pages/Platforms'));
const PlatformDetails = lazy(() => import('./pages/PlatformDetails'));
const Pricing = lazy(() => import('./pages/PricingNew'));
const Users = lazy(() => import('./pages/Users'));
const SupplierPayments = lazy(() => import('./pages/SupplierPayments'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Employees = lazy(() => import('./pages/Employees'));
const Manufacturing = lazy(() => import('./pages/Manufacturing'));
const ManufacturingDetail = lazy(() => import('./pages/ManufacturingDetail'));
const Challan = lazy(() => import('./pages/Challan'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Component to handle root path redirect based on user permissions
const RootRedirect = () => {
  const { user } = useAuth();
  const firstRoute = getFirstAccessibleRoute(user?.role);
  return <Navigate to={firstRoute} replace />;
};

function AppContent() {
  const { user, loading } = useAuth();

  // Health check background process - calls every 14 minutes
  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    const callHealthEndpoint = async () => {
      try {
        const response = await fetch(`${API_URL}/api/health`);
        if (response.ok) {
          console.log('Health check successful');
        }
      } catch (error) {
        console.error('Health check failed:', error);
      }
    };

    // Call immediately on mount
    callHealthEndpoint();

    // Set up interval for 14 minutes (840000 milliseconds)
    const intervalId = setInterval(callHealthEndpoint, 14 * 60 * 1000);

    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route 
          path="/login" 
          element={
            user ? <Navigate to={getFirstAccessibleRoute(user?.role)} replace /> : <Login />
          } 
        />
        <Route
          path="/*"
          element={
            <PrivateRoute>
              <MainLayout>
                <Suspense 
                  fallback={
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center', 
                      height: '60vh' 
                    }}>
                      <Spin size="large" tip="Loading..." />
                    </div>
                  }
                >
                  <Routes>
                    {/* Public/Error Routes */}
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    
                    {/* Root path - Redirect to first accessible route */}
                    <Route path="/" element={<RootRedirect />} />
                    
                    {/* Dashboard - Restricted for operations users */}
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute resource={RESOURCES.DASHBOARD}>
                          <Dashboard />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Products & Inventory - All authenticated users */}
                    <Route 
                      path="/products" 
                      element={
                        <ProtectedRoute resource={RESOURCES.PRODUCTS}>
                          <Products />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/inventory" 
                      element={
                        <ProtectedRoute resource={RESOURCES.INVENTORY}>
                          <Inventory />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Operations Routes - All authenticated users */}
                    <Route 
                      path="/sales" 
                      element={
                        <ProtectedRoute resource={RESOURCES.SALES}>
                          <Sales />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/returns" 
                      element={
                        <ProtectedRoute resource={RESOURCES.RETURNS}>
                          <Returns />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/purchases" 
                      element={
                        <ProtectedRoute resource={RESOURCES.PURCHASES}>
                          <Purchases />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/manufacturing" 
                      element={
                        <ProtectedRoute resource={RESOURCES.MANUFACTURING}>
                          <Manufacturing />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/manufacturing/:id" 
                      element={
                        <ProtectedRoute resource={RESOURCES.MANUFACTURING}>
                          <ManufacturingDetail />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/challan" 
                      element={
                        <ProtectedRoute resource={RESOURCES.CHALLAN}>
                          <Challan />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/platforms" 
                      element={
                        <ProtectedRoute resource={RESOURCES.PLATFORMS}>
                          <Platforms />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/platforms/:id" 
                      element={
                        <ProtectedRoute resource={RESOURCES.PLATFORMS}>
                          <PlatformDetails />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pricing" 
                      element={
                        <ProtectedRoute resource={RESOURCES.PRICING}>
                          <Pricing />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Finance Routes - Restricted for operations users */}
                    <Route 
                      path="/supplier-payments" 
                      element={
                        <ProtectedRoute resource={RESOURCES.SUPPLIER_PAYMENTS}>
                          <SupplierPayments />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/expenses" 
                      element={
                        <ProtectedRoute resource={RESOURCES.EXPENSES}>
                          <Expenses />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Employee Management - Restricted for operations users */}
                    <Route 
                      path="/employees" 
                      element={
                        <ProtectedRoute resource={RESOURCES.EMPLOYEES}>
                          <Employees />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* User Management - Admin only */}
                    <Route 
                      path="/users" 
                      element={
                        <ProtectedRoute resource={RESOURCES.USERS}>
                          <Users />
                        </ProtectedRoute>
                      } 
                    />
                  </Routes>
                </Suspense>
              </MainLayout>
            </PrivateRoute>
          }
        />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;

